import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../../components/ui/pagination';
import { Home, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 10;

export default function AdminShorts() {
  const navigate = useNavigate();
  const [shorts, setShorts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShort, setEditingShort] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [formData, setFormData] = useState({
    summary: '',
    parentType: 'article',
    parentId: '',
    thumbnailUrl: '',
    tags: '',
    status: 'draft'
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData(currentPage);
  }, [token, navigate, currentPage]);

  const fetchData = async (page) => {
    try {
      setLoading(true);
      const [shortsRes, articlesRes, videosRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/shorts?page=${page}&pageSize=${PAGE_SIZE}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/articles?status=published&pageSize=100`),
        axios.get(`${API_URL}/api/videos?status=published&pageSize=100`)
      ]);
      
      // Handle both paginated and non-paginated response formats
      if (shortsRes.data && shortsRes.data.items) {
        setShorts(shortsRes.data.items || []);
        setTotalPages(shortsRes.data.totalPages || 1);
        setTotalItems(shortsRes.data.total || 0);
      } else if (Array.isArray(shortsRes.data)) {
        setShorts(shortsRes.data);
        setTotalPages(1);
        setTotalItems(shortsRes.data.length);
      } else {
        setShorts([]);
        setTotalPages(1);
        setTotalItems(0);
      }
      
      setArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);
      setVideos(Array.isArray(videosRes.data) ? videosRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setShorts([]);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (editingShort) {
        await axios.put(`${API_URL}/api/shorts/${editingShort.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Short updated successfully');
      } else {
        await axios.post(`${API_URL}/api/shorts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Short created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData(currentPage);
    } catch (error) {
      console.error('Error saving short:', error);
      toast.error(error.response?.data?.detail || 'Failed to save short');
    }
  };

  const handleEdit = (short) => {
    setEditingShort(short);
    setFormData({
      summary: short.summary,
      parentType: short.parentType,
      parentId: short.parentId,
      thumbnailUrl: short.thumbnailUrl || '',
      tags: short.tags.join(', '),
      status: short.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this short?')) return;

    try {
      await axios.delete(`${API_URL}/api/shorts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Short deleted successfully');
      fetchData(currentPage);
    } catch (error) {
      console.error('Error deleting short:', error);
      toast.error('Failed to delete short');
    }
  };

  const resetForm = () => {
    setEditingShort(null);
    setFormData({
      summary: '',
      parentType: 'article',
      parentId: '',
      thumbnailUrl: '',
      tags: '',
      status: 'draft'
    });
  };

  const getParentTitle = (short) => {
    if (short.parentType === 'article') {
      const article = articles.find(a => a.id === short.parentId);
      return article?.title || 'Unknown Article';
    } else {
      const video = videos.find(v => v.id === short.parentId);
      return video?.title || 'Unknown Video';
    }
  };

  const parentOptions = formData.parentType === 'article' ? articles : videos;

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Manage Shorts · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Shorts</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Manage Shorts</h2>
              <p className="text-muted-foreground">
                {totalItems} total shorts · Page {currentPage} of {totalPages}
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-short-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Short
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingShort ? 'Edit Short' : 'Create Short'}</DialogTitle>
                  <DialogDescription>Fill in the short details below</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="summary">Summary (max 400 characters)</Label>
                    <Textarea
                      id="summary"
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      rows={4}
                      maxLength={400}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.summary.length}/400 characters
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="parentType">Parent Type</Label>
                    <Select 
                      value={formData.parentType} 
                      onValueChange={(value) => setFormData({...formData, parentType: value, parentId: ''})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="article">Article</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="parentId">Parent {formData.parentType === 'article' ? 'Article' : 'Video'}</Label>
                    <Select value={formData.parentId} onValueChange={(value) => setFormData({...formData, parentId: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a parent..." />
                      </SelectTrigger>
                      <SelectContent>
                        {parentOptions.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="thumbnailUrl">Thumbnail URL (optional)</Label>
                    <Input
                      id="thumbnailUrl"
                      type="url"
                      value={formData.thumbnailUrl}
                      onChange={(e) => setFormData({...formData, thumbnailUrl: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      placeholder="branding, marketing"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit">{editingShort ? 'Update' : 'Create'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <>
              <div className="grid gap-4" data-testid="shorts-list">
                {shorts.map((short) => (
                  <Card key={short.id} className="p-4 bg-[hsl(var(--surface-2))] border-border">
                    <div className="flex items-start gap-4">
                      {short.thumbnailUrl && (
                        <img src={short.thumbnailUrl} alt="" className="w-24 h-24 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={short.status === 'published' ? 'default' : 'secondary'}>
                            {short.status}
                          </Badge>
                          <Badge variant="outline">{short.parentType}</Badge>
                        </div>
                        <p className="text-sm text-foreground mb-2 line-clamp-2">{short.summary}</p>
                        <p className="text-xs text-muted-foreground">
                          Parent: {getParentTitle(short)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(short)} data-testid={`edit-short-${short.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(short.id)} data-testid={`delete-short-${short.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers()[0] > 1 && (
                        <>
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {getPageNumbers()[0] > 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                        </>
                      )}
                      
                      {getPageNumbers().map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink 
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                        <>
                          {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(totalPages)} className="cursor-pointer">
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
